import React from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ShieldCheck, Heart, Target, Compass, Globe, Mail, Sparkles } from "lucide-react";

const OverOns = () => {
  const { t } = useTranslation();

  const stats = [
    { v: "65+", l: t("over.stat_services") },
    { v: "9", l: t("over.stat_categories") },
    { v: "200+", l: t("over.stat_reviews") },
    { v: "4.9★", l: t("over.stat_rating") },
  ];

  const values = [
    { icon: ShieldCheck, t: t("over.v1_t"), d: t("over.v1_d"), tint: "bg-emerald-50 text-emerald-600 border-emerald-200" },
    { icon: Heart, t: t("over.v2_t"), d: t("over.v2_d"), tint: "bg-rose-50 text-rose-600 border-rose-200" },
    { icon: Target, t: t("over.v3_t"), d: t("over.v3_d"), tint: "bg-blue-50 text-blue-600 border-blue-200" },
    { icon: Compass, t: t("over.v4_t"), d: t("over.v4_d"), tint: "bg-amber-50 text-[#F5A623] border-amber-200" },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-50/60 via-white to-white" />
          <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#F5A623]/15 blur-3xl" />
          <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#1A2B4A]/10 blur-3xl" />
        </div>
        <div className="max-w-6xl mx-auto px-4 md:px-8 pt-14 md:pt-24 pb-12 md:pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="pill-amber">{t("over.badge")}</span>
              <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-[#1A2B4A] mt-4 leading-[1.05] tracking-tight">
                {t("over.hero_1")} <span className="text-[#F5A623]">{t("over.hero_highlight")}</span> {t("over.hero_2")}
              </h1>
              <p className="text-lg text-slate-600 mt-5">{t("over.hero_sub")}</p>
            </div>
            <div className="relative">
              <div className="beecenter-card overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/3184433/pexels-photo-3184433.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Team collaborating"
                  className="w-full h-72 md:h-[420px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#1A2B4A] text-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 md:py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <div key={i} className="text-center" data-testid={`stat-${i}`}>
                <div className="font-heading text-4xl md:text-5xl font-extrabold text-[#F5A623] tracking-tight">{s.v}</div>
                <div className="text-xs uppercase tracking-wider text-slate-300 mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MISSIE / STORY */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 md:px-8 grid md:grid-cols-5 gap-10 items-start">
          <div className="md:col-span-2">
            <span className="text-xs uppercase tracking-wider text-[#F5A623] font-bold">{t("over.mission_label")}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2B4A] mt-2 tracking-tight">{t("over.mission_title")}</h2>
          </div>
          <div className="md:col-span-3 space-y-5 text-slate-700 leading-relaxed">
            <p>{t("over.mission_p1")}</p>
            <p>{t("over.mission_p2")}</p>
            <p>{t("over.mission_p3")}</p>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-[#F8FAFC] py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="pill-amber">{t("over.values_badge")}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2B4A] mt-3 tracking-tight">{t("over.values_title")}</h2>
            <p className="text-slate-600 mt-4">{t("over.values_subtitle")}</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} className="beecenter-card p-6 hover:shadow-md transition-all" data-testid={`value-${i}`}>
                  <div className={`w-12 h-12 rounded-xl ${v.tint} border flex items-center justify-center`}>
                    <Icon size={22} />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-[#1A2B4A] mt-4">{v.t}</h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{v.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PARENT BRAND */}
      <section className="py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="beecenter-card p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F5A623]/10 text-[#1A2B4A] text-xs font-bold uppercase tracking-wider">
                <Globe size={12} className="text-[#F5A623]" /> {t("over.parent_label")}
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A2B4A] mt-3 tracking-tight">{t("over.parent_title")}</h2>
              <p className="text-slate-600 mt-4">{t("over.parent_p1")}</p>
              <p className="text-slate-600 mt-3">{t("over.parent_p2")}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[Sparkles, ShieldCheck, Heart, Target].map((Icon, i) => (
                <div key={i} className="aspect-square rounded-2xl bg-gradient-to-br from-amber-50 via-white to-slate-50 ring-1 ring-slate-200 flex items-center justify-center">
                  <Icon size={42} className="text-[#F5A623] opacity-30" strokeWidth={1.4} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1A2B4A] text-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight">{t("over.cta_title")}</h2>
          <p className="text-slate-300 mt-4 text-lg">{t("over.cta_subtitle")}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/onboarding" className="btn-amber" data-testid="over-cta-start">
              {t("over.cta_btn_klant")} <ArrowRight size={16} />
            </Link>
            <a href="mailto:info@beecentre.nl" className="btn-outline-navy !text-white !border-white hover:!bg-white hover:!text-[#1A2B4A]" data-testid="over-cta-contact">
              <Mail size={16} /> {t("over.cta_btn_contact")}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OverOns;
